import { assign } from 'xstate';

import { filterFreeAdditions, getFreeAdditionGroups } from '../../helpers';
import {
  type CustomizationMachineAction,
  type Modifier,
  type SubstitutionModifier,
} from '../../types';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Calculates the net price based on the provided customization context.
 *
 * It processes modifiers such as additions, removals, and substitutions
 * by filtering, calculating costs, and summing up their respective values to derive the net price.
 *
 * It also considers rules for free additions.
 */
export const calculateNetPrice: CustomizationMachineAction = assign((args) => {
  const { context } = args;
  const { allModificationsMap, modifications, modificationLimits } = context;

  if (!modifications || !allModificationsMap) return {};

  const { additions, removals, substitutions } = modifications;

  const freeAdditionGroups = getFreeAdditionGroups(modificationLimits);
  const filteredAdditions = filterFreeAdditions(additions, allModificationsMap, freeAdditionGroups); // prettier-ignore

  const additionsTotalCost = filteredAdditions.reduce(calculateAdditionCost, 0);
  const removalsTotalCost = removals.reduce(calculateRemovalCost, 0);
  const substitutionsTotalCost = substitutions.reduce(calculateSubstitutionCost, 0); // prettier-ignore
  const netPrice = additionsTotalCost + removalsTotalCost + substitutionsTotalCost; // prettier-ignore

  return { netPrice };
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function calculateAdditionCost(total: number, addition: Modifier) {
  return total + addition.additionCost;
}

function calculateRemovalCost(total: number, removal: Modifier) {
  return total + removal.removalCost;
}

function calculateSubstitutionCost(total: number, substitution: SubstitutionModifier) {
  return total + substitution.removalCost + substitution.substitutionCost;
}
